import { SQL } from '@/lib/Sqlite'
import { syncQueue, JobStatus } from './index'

// 해당 항목의 동기화 job이 큐에서 대기(PENDING)·진행(PROCESSING) 중인지 확인
export function hasInFlightJob(localStringId: string): boolean {
  return syncQueue
    .getQueueState()
    .some(
      job =>
        job.localStringId === localStringId &&
        (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING),
    )
}

/**
 * 해당 row가 로컬에서 삭제(soft-delete) 상태인지 확인.
 * 업로드 완료 처리 직전에 호출해, 업로드 도중 삭제가 끼어든 경우
 * modifiedFromLocal을 유지시켜 삭제가 이어서 서버로 전파되게 한다.
 * @param storeKey 테이블 이름
 * @param localStringId 대상 row의 localStringId
 */
export async function isRowDeletedLocally(
  storeKey: string,
  localStringId: string,
): Promise<boolean> {
  const db = await SQL.getDatabase()
  const result = await db.query(`SELECT deleted_at FROM ${storeKey} WHERE localStringId = ?`, [
    localStringId,
  ])
  const deletedAt = result.values?.[0]?.deleted_at
  return deletedAt !== null && deletedAt !== undefined && deletedAt !== ''
}

// fields_updated_at을 비교 가능한 정규 문자열로 변환 (문자열/객체/누락/파싱 실패 모두 방어)
export function normalizeFieldUpdates(raw: unknown): string {
  let parsed: Record<string, unknown> = {}
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>
    } catch {
      parsed = {}
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw as Record<string, unknown>
  }
  const sorted: Record<string, unknown> = {}
  for (const key of Object.keys(parsed).sort()) sorted[key] = parsed[key]
  return JSON.stringify(sorted)
}

/**
 * job 실행용 스냅샷 이후 해당 row에 로컬 수정이 끼어들었는지 확인 (fields_updated_at 비교).
 * 업로드 완료 처리 직전에 호출해, 파일 업로드 도중 대표사진 변경 등이 끼어든 경우
 * modifiedFromLocal을 유지시켜 후속 update job이 변경을 서버로 전파하게 한다.
 * @param storeKey 테이블 이름
 * @param localStringId 대상 row의 localStringId
 * @param snapshotFieldsUpdatedAt job 실행 시점 데이터의 fields_updated_at
 */
export async function hasLocalChangesSince(
  storeKey: string,
  localStringId: string,
  snapshotFieldsUpdatedAt: unknown,
): Promise<boolean> {
  const db = await SQL.getDatabase()
  const result = await db.query(
    `SELECT fields_updated_at FROM ${storeKey} WHERE localStringId = ?`,
    [localStringId],
  )
  if (!result.values || result.values.length === 0) return false
  return (
    normalizeFieldUpdates(result.values[0].fields_updated_at) !==
    normalizeFieldUpdates(snapshotFieldsUpdatedAt)
  )
}
